// Resource Management — weekly capacity grid per station (stage),
// plus a project timeline strip beneath. Replaces the per-assignee
// snapshot the file used to host (BSP's prior "Resource" tab).
//
// Designed for non-technical workshop staff: every cell shows the
// scheduled hours and a colour cue for utilisation (<40 cool / 40-80
// good / 80-100 warning / >100 over). The "Unassigned" row surfaces
// stage hours that don't yet have start/done dates so the foreman
// can see how much is still to slot in. The Team Total row sums
// across stations.
//
// Pagination: from-date is a Mon-anchored ISO; the weeks selector
// is fixed at 12 for now (matches the screenshot density).
import { useMemo, useState } from "react";
import type { ResourceLoadResponse } from "../../../../../../shared/types/production-schedule";
import { useResourceLoad } from "../hooks/useProductionApi";

interface Props {
  onOpenJob(id: number): void;
}

// Same Mon-anchor logic the server uses — keeps the from-date
// always landing on a week start so the grid never shows a half
// column at the leading edge.
function weekStartIso(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = d.getUTCDay();
  const back = dow === 0 ? 6 : dow - 1;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// Utilisation tints. Light enough to keep text readable; thresholds
// match the legend on the screenshot.
function tintFor(pct: number): { background: string; color: string; label: string } {
  if (pct >= 100) return { background: "#FDE7E5", color: "#A4221C", label: ">100%" };
  if (pct >= 80) return { background: "#FFF2D4", color: "#8A5B00", label: "80-100%" };
  if (pct >= 40) return { background: "#DEF4E2", color: "#1E6F3A", label: "40-80%" };
  return { background: "#EAEAFB", color: "#3B3D8C", label: "<40%" };
}

// Group week buckets by their month (or month-and-month for cross-
// month groups) so the top header reads "APR 2026" / "MAY 2026".
function monthGroups(weeks: { start: string; end: string }[]): { label: string; span: number }[] {
  const out: { label: string; span: number }[] = [];
  for (const w of weeks) {
    const label = new Date(`${w.start}T00:00:00Z`).toLocaleString("en-AU", {
      month: "short",
      year: "numeric",
    }).toUpperCase();
    const last = out[out.length - 1];
    if (last && last.label === label) {
      last.span += 1;
    } else {
      out.push({ label, span: 1 });
    }
  }
  return out;
}

function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleString("en-AU", { day: "numeric", month: "short" }).toUpperCase();
}

export function ResourceView({ onOpenJob }: Props) {
  const [fromIso, setFromIso] = useState(weekStartIso(todayIso()));
  const weeks = 12;
  const { data, error, loading } = useResourceLoad(fromIso, weeks);

  if (loading) {
    return <div style={{ padding: 18, fontSize: 12, color: "var(--bsp-fg-muted)" }}>Loading capacity…</div>;
  }
  if (error || !data) {
    return (
      <div style={{ padding: 18, fontSize: 12, color: "var(--bsp-red-fg)" }}>
        {error ?? "No data"}
      </div>
    );
  }

  return (
    <div>
      <ResourceHeader
        data={data}
        fromIso={fromIso}
        onPrev={() => setFromIso(addDays(fromIso, -7 * weeks))}
        onNext={() => setFromIso(addDays(fromIso, 7 * weeks))}
        onToday={() => setFromIso(weekStartIso(todayIso()))}
      />
      <ResourceGrid data={data} />
      <ProjectTimelines data={data} onOpenJob={onOpenJob} />
    </div>
  );
}

function ResourceHeader({
  data,
  fromIso,
  onPrev,
  onNext,
  onToday,
}: {
  data: ResourceLoadResponse;
  fromIso: string;
  onPrev(): void;
  onNext(): void;
  onToday(): void;
}) {
  void fromIso;
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      padding: "10px 0",
      marginBottom: 8,
    }}>
      <div style={{ display: "flex", gap: 4 }}>
        <button type="button" className="btn-sm" onClick={onPrev} aria-label="Previous window">←</button>
        <button type="button" className="btn-sm" onClick={onToday}>Today</button>
        <button type="button" className="btn-sm" onClick={onNext} aria-label="Next window">→</button>
      </div>
      <Legend />
      <div style={{ marginLeft: "auto", display: "flex", gap: 12, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
        <span><strong style={{ color: "var(--bsp-fg)" }}>{data.totals.openJobs}</strong> OPEN</span>
        <span>·</span>
        <span><strong style={{ color: "var(--bsp-fg)" }}>{data.totals.stationCount}</strong> STATIONS</span>
        <span>·</span>
        <span><strong style={{ color: "var(--bsp-fg)" }}>{data.totals.capacityPerWeek}H</strong>/WK</span>
      </div>
    </div>
  );
}

function Legend() {
  const items = [
    { range: "<40%", pct: 20 },
    { range: "40-80%", pct: 60 },
    { range: "80-100%", pct: 90 },
    { range: ">100%", pct: 110 },
  ];
  return (
    <div style={{ display: "flex", gap: 6, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
      {items.map((it) => {
        const t = tintFor(it.pct);
        return (
          <span
            key={it.range}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 6px",
              borderRadius: 999,
              background: t.background,
              color: t.color,
              fontWeight: 600,
            }}
          >
            {it.range}
          </span>
        );
      })}
    </div>
  );
}

function ResourceGrid({ data }: { data: ResourceLoadResponse }) {
  const groups = useMemo(() => monthGroups(data.weeks), [data.weeks]);
  const today = data.todayIso;
  const todayCol = data.weeks.findIndex((w) => today >= w.start && today <= w.end);
  const stationCol = 240;
  const cellMin = 76;

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--bsp-border)", borderRadius: 4 }}>
      <table style={{
        borderCollapse: "collapse",
        width: "100%",
        minWidth: stationCol + data.weeks.length * cellMin,
        fontSize: 11,
      }}>
        <thead>
          <tr>
            <th style={headStyle(stationCol)}>MONTH</th>
            {groups.map((g, i) => (
              <th
                key={`${g.label}:${i}`}
                colSpan={g.span}
                style={{
                  ...headStyle(),
                  textAlign: "center",
                  background: "var(--bsp-surface-muted)",
                  fontWeight: 700,
                }}
              >
                {g.label}
              </th>
            ))}
          </tr>
          <tr>
            <th style={{ ...headStyle(stationCol), textTransform: "uppercase", fontSize: 10 }}>Station</th>
            {data.weeks.map((w, i) => (
              <th
                key={w.start}
                style={{
                  ...headStyle(cellMin),
                  textAlign: "center",
                  fontFamily: "var(--bsp-font-mono)",
                  background: i === todayCol ? "rgba(231, 76, 60, 0.08)" : undefined,
                }}
                title={`${w.start} – ${w.end}`}
              >
                {shortDate(w.start)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.stations.map((s) => (
            <tr key={s.stageKey}>
              <td style={cellStyle(stationCol)}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)" }}>
                      {s.weeklyCapacityHours}h/wk capacity
                    </div>
                  </div>
                </div>
              </td>
              {s.weeks.map((cell, i) => (
                <td key={`${s.stageKey}:${i}`} style={dataCellStyle(cell.utilisationPct, i === todayCol)}>
                  {cell.hours > 0 ? (
                    <>
                      <div style={{ fontWeight: 700 }}>{cell.hours}h</div>
                      <div style={{ fontSize: 10, opacity: 0.75 }}>{cell.utilisationPct}%</div>
                    </>
                  ) : (
                    <span style={{ color: "var(--bsp-fg-muted)" }}>—</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td style={{ ...cellStyle(stationCol), background: "#FFF7E5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#FFD27A", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#8A5B00" }}>?</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#8A5B00", fontWeight: 700 }}>Unassigned</div>
                  <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)" }}>
                    {totalUnassigned(data)}h with no start/done dates
                  </div>
                </div>
              </div>
            </td>
            {data.weeks.map((_, i) => (
              <td key={i} style={{ ...cellStyle(), background: "rgba(255, 232, 178, 0.4)", textAlign: "center", color: "var(--bsp-fg-muted)" }}>
                —
              </td>
            ))}
          </tr>
          <tr>
            <td style={{ ...cellStyle(stationCol), background: "var(--bsp-surface-muted)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--bsp-blue)", color: "white", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>Σ</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>Team Total</div>
                  <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)" }}>{data.totals.capacityPerWeek}h/wk capacity</div>
                </div>
              </div>
            </td>
            {data.totals.weeks.map((cell, i) => (
              <td key={i} style={dataCellStyle(cell.utilisationPct, i === todayCol, true)}>
                {cell.hours > 0 ? (
                  <>
                    <div style={{ fontWeight: 700 }}>{cell.hours}h</div>
                    <div style={{ fontSize: 10, opacity: 0.75 }}>{cell.utilisationPct}%</div>
                  </>
                ) : (
                  <span style={{ color: "var(--bsp-fg-muted)" }}>—</span>
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function totalUnassigned(data: ResourceLoadResponse): number {
  let sum = 0;
  for (const s of data.stations) sum += s.unassignedHours;
  return Math.round(sum * 100) / 100;
}

function ProjectTimelines({
  data,
  onOpenJob,
}: {
  data: ResourceLoadResponse;
  onOpenJob(id: number): void;
}) {
  const windowStart = data.weeks[0]?.start ?? data.todayIso;
  const windowEnd = data.weeks[data.weeks.length - 1]?.end ?? data.todayIso;
  // Only show projects whose [start, end] overlap the visible
  // window, sorted by start (earliest first), so the bars line up
  // naturally under the grid.
  const visible = useMemo(
    () => data.projects
      .filter((p) => p.startDate && p.endDate && p.endDate >= windowStart && p.startDate <= windowEnd)
      .sort((a, b) => (a.startDate ?? "").localeCompare(b.startDate ?? "")),
    [data.projects, windowStart, windowEnd],
  );
  if (visible.length === 0) {
    return (
      <div style={{ marginTop: 18, padding: 14, fontSize: 11, color: "var(--bsp-fg-muted)", border: "1px solid var(--bsp-border)", borderRadius: 4 }}>
        No projects scheduled in this window.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        fontFamily: "var(--bsp-font-display)",
        fontSize: 11,
        fontWeight: 700,
        color: "var(--bsp-blue)",
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        marginBottom: 8,
      }}>Project Timelines</div>
      <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 6, fontSize: 11 }}>
        {visible.map((p) => (
          <ProjectRow
            key={p.jobId}
            project={p}
            windowStart={windowStart}
            windowEnd={windowEnd}
            todayIso={data.todayIso}
            onOpen={() => onOpenJob(p.jobId)}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectRow({
  project: p,
  windowStart,
  windowEnd,
  todayIso,
  onOpen,
}: {
  project: ResourceLoadResponse["projects"][number];
  windowStart: string;
  windowEnd: string;
  todayIso: string;
  onOpen(): void;
}) {
  // Total window in days; bar position + length is a % of that span.
  const totalDays = Math.max(1, daysBetween(windowStart, windowEnd) + 1);
  const start = p.startDate ?? windowStart;
  const end = p.endDate ?? windowEnd;
  const visStart = start < windowStart ? windowStart : start;
  const visEnd = end > windowEnd ? windowEnd : end;
  const offsetDays = Math.max(0, daysBetween(windowStart, visStart));
  const lenDays = Math.max(1, daysBetween(visStart, visEnd) + 1);
  const leftPct = (offsetDays / totalDays) * 100;
  const widthPct = (lenDays / totalDays) * 100;
  const todayPct = todayIso >= windowStart && todayIso <= windowEnd
    ? (daysBetween(windowStart, todayIso) / totalDays) * 100
    : null;
  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        style={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
          padding: "4px 6px",
          fontSize: 11,
          color: "var(--bsp-fg)",
        }}
        title="Open job"
      >
        <div style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700 }}>{p.jobNumber}</div>
        <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 230 }}>
          {p.jobName}
        </div>
      </button>
      <div style={{ position: "relative", padding: "6px 0" }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 1, background: "var(--bsp-border)" }} />
        <div
          style={{
            position: "absolute",
            top: 4,
            bottom: 4,
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            background: `${p.color}33`,
            borderLeft: `3px solid ${p.color}`,
            borderRadius: 3,
            padding: "0 6px",
            display: "flex",
            alignItems: "center",
            fontSize: 10,
            color: p.color,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
          title={`${p.startDate} → ${p.endDate} · ${p.totalHours}h · ${p.status}`}
        >
          {p.status} · {p.totalHours}h
        </div>
        {todayPct !== null ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${todayPct}%`,
              width: 2,
              background: "#E74C3C",
            }}
          />
        ) : null}
      </div>
    </>
  );
}

function daysBetween(a: string, b: string): number {
  const ad = new Date(`${a}T00:00:00Z`).getTime();
  const bd = new Date(`${b}T00:00:00Z`).getTime();
  return Math.round((bd - ad) / 86_400_000);
}

// ── Style helpers ───────────────────────────────────────────────────
function headStyle(width?: number): React.CSSProperties {
  return {
    padding: "6px 10px",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--bsp-fg-muted)",
    borderBottom: "1px solid var(--bsp-border)",
    background: "var(--bsp-surface)",
    minWidth: width,
    position: "sticky",
    top: 0,
  };
}

function cellStyle(width?: number): React.CSSProperties {
  return {
    padding: "8px 10px",
    borderBottom: "1px solid var(--bsp-border)",
    borderRight: "1px solid var(--bsp-border)",
    verticalAlign: "middle",
    minWidth: width,
  };
}

function dataCellStyle(pct: number, todayCol: boolean, total = false): React.CSSProperties {
  if (pct === 0) {
    return {
      ...cellStyle(),
      textAlign: "center",
      background: todayCol ? "rgba(231, 76, 60, 0.06)" : (total ? "var(--bsp-surface-muted)" : "var(--bsp-surface)"),
      color: "var(--bsp-fg-muted)",
    };
  }
  const t = tintFor(pct);
  return {
    ...cellStyle(),
    textAlign: "center",
    background: t.background,
    color: t.color,
    fontFamily: "var(--bsp-font-mono)",
  };
}
